import { useEffect, useRef } from 'react';
import { useAuth } from '@screentone/addon-auth-wrapper';
import { Box, Button, IconSearch, Overlay, useModalPortal, useModalState } from '@screentone/core';

import { SnippetyImageData } from 'hooks/useSnippetyQuery';
import { IMAGE_MANAGER_DOMAIN } from 'utils/constants';
import styles from './ImageManagerEmbed.module.scss';

interface ImageManagerEmbedProps {
  callback: (data: SnippetyImageData) => void;
  disabled?: boolean;
}

/**
 * Encapsulates the Image Manager iframe embed: builds the Image Manager URL,
 * generates a unique ref for the postMessage handshake, and opens/closes the
 * modal. Consumers only need to provide a callback to receive the selected
 * image payload - they don't need to know how the iframe communicates back.
 */
export const ImageManagerEmbed = ({ disabled = false, callback, ...otherButtonProps }: ImageManagerEmbedProps) => {
  const { currentProperty } = useAuth();
  const { renderNode } = useModalPortal();
  const { open, openModal, closeModal } = useModalState();
  const itemRef = useRef(`image-manager-${Date.now()}`);

  useEffect(() => {
    function handleMessage(event: MessageEvent<string>) {
      try {
        const data = JSON.parse(event.data) as SnippetyImageData;
        if (data.ref === itemRef.current) {
          callback(data);
          closeModal();
        }
      } catch (error) {
        console.error('Error parsing message data:', event.data, error);
      }
    }

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [callback, closeModal]);

  return (
    <>
      <Button
        tertiary
        disabled={disabled}
        onClick={openModal}
        icon={IconSearch}
        data-testid="search-option"
        size='sm'
        {...otherButtonProps}
      >
        Search
      </Button>
      {open && (
        <Overlay onDismiss={closeModal} renderNode={renderNode} className={styles.overlay} status="open">
          <Box className={styles.box}>
            <Box.Title className={styles.title} data-testid="image-manager-embed-modal-title">
              Image Manager
            </Box.Title>
            <Box.Content padding={{ all: 'none' }} className={styles.content}>
              <iframe
                frameBorder="0"
                className={styles.iframe}
                title="Image Manager"
                src={`${IMAGE_MANAGER_DOMAIN}/${currentProperty ?? ''}/?ref=${itemRef.current}&entry=search`}
                data-testid="image-manager-embed-modal-content"
                allow="clipboard-write *;"
              />
            </Box.Content>
          </Box>
        </Overlay>
      )}
    </>
  );
};
